import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import { Plus, Edit, Trash2, QrCode, MapPin, Phone, Table, Store } from 'lucide-react';
import { API } from '../../../lib/api';

const PartnersListTab = ({ partners, agents, tables, getAuthHeaders, onRefresh, onShowQR }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'Bar', address: '', agent_id: '', table_id: '' });
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setForm({ name: '', type: 'Bar', address: '', agent_id: '', table_id: '' });
    setEditingPartner(null);
    setShowForm(false);
  };

  const openEdit = (p) => {
    setForm({ name: p.name, type: p.type, address: p.address || '', agent_id: p.agent_id, table_id: p.table_id });
    setEditingPartner(p);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.agent_id || !form.table_id) {
      toast.error('Selecione um agente e uma tabela de investimento');
      return;
    }
    setSaving(true);
    try {
      if (editingPartner) {
        await axios.put(`${API}/partners/${editingPartner.id}`, form, getAuthHeaders());
        toast.success('Parceiro atualizado!');
      } else {
        await axios.post(`${API}/partners`, form, getAuthHeaders());
        toast.success('Parceiro criado!');
      }
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar parceiro');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este parceiro?')) return;
    try {
      await axios.delete(`${API}/partners/${id}`, getAuthHeaders());
      toast.success('Parceiro excluído');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleToggleActive = async (p) => {
    try {
      await axios.put(`${API}/partners/${p.id}`, { active: !p.active }, getAuthHeaders());
      toast.success(p.active ? 'Parceiro desativado' : 'Parceiro ativado');
      onRefresh();
    } catch {
      toast.error('Erro ao alterar status');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Parceiros ({partners.length})</h2>
        <Button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showForm && !editingPartner ? 'Cancelar' : 'Novo Parceiro'}
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 border-orange-200 bg-orange-50/30">
          <h3 className="font-bold text-gray-800 mb-4">{editingPartner ? 'Editar Parceiro' : 'Novo Parceiro'}</h3>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Nome do Estabelecimento *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required placeholder="Ex: Bar do Zé"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Tipo *</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  {['Bar', 'Restaurante', 'Lanchonete', 'Pub', 'Churrascaria', 'Pizzaria', 'Sorveteria', 'Outro'].map(t => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Endereço</label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Rua, número, bairro..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Agente Responsável *</label>
                <select
                  value={form.agent_id}
                  onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Selecionar agente...</option>
                  {agents.map(a => (
                    <option key={a.id} value={a.id}>{a.name} — {a.whatsapp_number}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Tabela de Investimento *</label>
                <select
                  value={form.table_id}
                  onChange={(e) => setForm({ ...form, table_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Selecionar tabela...</option>
                  {tables.map(t => (
                    <option key={t.id} value={t.id}>{t.name} ({t.ranges?.length || 0} faixas)</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" onClick={resetForm} variant="outline" className="flex-1">Cancelar</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">
                {saving ? 'Salvando...' : editingPartner ? 'Salvar Alterações' : 'Criar Parceiro'}
              </Button>
            </div>
            {agents.length === 0 && (
              <p className="text-amber-600 text-xs">⚠️ Cadastre ao menos um agente na aba Agentes antes de criar parceiros.</p>
            )}
            {tables.length === 0 && (
              <p className="text-amber-600 text-xs">⚠️ Cadastre ao menos uma tabela de investimento antes de criar parceiros.</p>
            )}
          </form>
        </Card>
      )}

      <div className="space-y-3">
        {partners.length === 0 && !showForm && (
          <Card className="p-12 text-center">
            <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum parceiro cadastrado ainda.</p>
          </Card>
        )}
        {partners.map((p) => (
          <Card key={p.id} className={`p-5 ${!p.active ? 'opacity-60' : ''}`}>
            <div className="flex flex-wrap gap-4 items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-bold text-gray-800">{p.name}</h3>
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">{p.type}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${p.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {p.active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                  {p.address && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{p.address}</span>}
                  <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />Agente: {p.agent_name}</span>
                  <span className="flex items-center gap-1"><Table className="w-3.5 h-3.5" />Tabela: {p.table_name}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1 font-mono">/parceiro/{p.slug}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center hidden sm:block">
                  <p className="text-2xl font-bold text-orange-600">{p.total_scans || 0}</p>
                  <p className="text-xs text-gray-400">Scans</p>
                </div>
                <div className="text-center hidden sm:block">
                  <p className="text-2xl font-bold text-green-600">{p.total_leads || 0}</p>
                  <p className="text-xs text-gray-400">Leads</p>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => onShowQR(p)} title="Ver QR Code"
                    className="p-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                  >
                    <QrCode className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openEdit(p)} title="Editar"
                    className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(p)}
                    className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-xs font-medium px-3"
                  >
                    {p.active ? 'Pausar' : 'Ativar'}
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)} title="Excluir"
                    className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PartnersListTab;
